import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';
import { ShieldCheck, Check, X } from 'lucide-react';

const ALL_CATEGORIES = '__all__';
const ALL_LOCATIONS = '__all__';

/**
 * Directory filter bar. Category options come from /api/service-categories; municipality
 * options from the shared Bohol location list (matches how service_area is stored).
 *
 * Filters auto-apply: discrete controls (category/municipality/verified) refetch on change
 * via a filters effect in Services.js; the text + rate inputs are debounced there too.
 */
const ProListingFilters = ({ categories, filters, setFilters, onClear, filtersActive }) => {
  const { t } = useTranslation();
  const set = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const categoryActive = Boolean(filters.category);
  const municipalityActive = Boolean(filters.municipality);
  const pillBase = 'rounded-full h-auto w-full sm:w-auto border px-3 py-1.5 text-sm';
  const pillActive = 'bg-primary text-primary-foreground border-primary';
  const pillIdle = 'bg-background text-foreground';

  return (
    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
      <div className="col-span-2 sm:flex-1 sm:min-w-[180px]">
        <Input
          value={filters.q}
          onChange={(e) => set({ q: e.target.value })}
          placeholder={t('services.searchPlaceholder')}
          aria-label={t('services.searchPlaceholder')}
        />
      </div>

      <Select
        value={filters.category || ALL_CATEGORIES}
        onValueChange={(v) => set({ category: v === ALL_CATEGORIES ? '' : v })}
      >
        <SelectTrigger
          aria-label={t('services.category')}
          className={`${pillBase} ${categoryActive ? pillActive : pillIdle}`}
        >
          <SelectValue placeholder={t('services.allCategories')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_CATEGORIES}>{t('services.allCategories')}</SelectItem>
          {categories.map((c) => (
            <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.municipality || ALL_LOCATIONS}
        onValueChange={(v) => set({ municipality: v === ALL_LOCATIONS ? '' : v })}
      >
        <SelectTrigger
          aria-label={t('services.municipality')}
          className={`${pillBase} ${municipalityActive ? pillActive : pillIdle}`}
        >
          <SelectValue placeholder={t('services.allLocations')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_LOCATIONS}>{t('services.allLocations')}</SelectItem>
          {BOHOL_MUNICIPALITIES.map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="col-span-2 flex gap-2 sm:w-auto">
        <Input
          type="number"
          min="0"
          className="flex-1 sm:w-24"
          value={filters.rate_min}
          onChange={(e) => set({ rate_min: e.target.value })}
          placeholder={t('services.rateMinShort')}
          aria-label={t('services.rateMin')}
        />
        <Input
          type="number"
          min="0"
          className="flex-1 sm:w-24"
          value={filters.rate_max}
          onChange={(e) => set({ rate_max: e.target.value })}
          placeholder={t('services.rateMaxShort')}
          aria-label={t('services.rateMax')}
        />
      </div>

      <button
        type="button"
        onClick={() => set({ verified_only: !filters.verified_only })}
        aria-pressed={filters.verified_only}
        className={`col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${pillBase} ${filters.verified_only ? pillActive : pillIdle}`}
      >
        <span
          aria-hidden="true"
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${filters.verified_only ? 'border-primary-foreground bg-primary-foreground text-primary' : 'border-input'}`}
        >
          {filters.verified_only && <Check size={12} />}
        </span>
        <ShieldCheck size={14} aria-hidden="true" />
        {t('services.verifiedOnly')}
      </button>

      {filtersActive && (
        <Button
          type="button"
          variant="ghost"
          onClick={onClear}
          className="col-span-2 sm:col-span-1 rounded-full h-auto px-3 py-1.5 text-sm"
        >
          <X size={14} className="mr-1" aria-hidden="true" />
          {t('services.clearFilters')}
        </Button>
      )}
    </div>
  );
};

export default ProListingFilters;
